import React from "react";
import { Link } from "react-router-dom";
import { Download, FileText } from "lucide-react";
import { useSEO } from "@/lib/seo";

const B = "/brand";

const slug = (p) => p.split("/").pop().replace(/\./g, "-");

const AssetCard = ({ title, note, preview, dark = false, downloads }) => (
  <div className="border border-line rounded-[16px] overflow-hidden bg-white" style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
    <div className={`flex items-center justify-center p-8 min-h-[140px] ${dark ? "bg-ink" : "bg-paper"}`}>
      <img src={preview} alt={title} className="max-h-20 max-w-full w-auto" />
    </div>
    <div className="p-5 border-t border-line">
      <p className="font-display font-black text-lg uppercase text-ink leading-none">{title}</p>
      {note && <p className="mt-2 text-sm text-muted-ink leading-relaxed">{note}</p>}
      <div className="mt-4 flex flex-wrap gap-2">
        {downloads.map((d) => (
          <a
            key={d.href}
            href={d.href}
            download
            data-testid={`marque-download-${slug(d.href)}`}
            className="inline-flex items-center gap-1.5 min-h-[44px] border border-ink text-ink text-xs font-bold uppercase tracking-wide px-4 py-2.5 rounded-[8px] hover:bg-ink hover:text-paper transition-colors duration-200"
          >
            <Download size={13} /> {d.label}
          </a>
        ))}
      </div>
    </div>
  </div>
);

const Group = ({ title, children }) => (
  <section className="mt-14">
    <h2 className="font-display font-black text-2xl md:text-3xl uppercase text-ink leading-none border-b-2 border-ink pb-3">
      {title}
    </h2>
    <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
  </section>
);

export default function Marque() {
  useSEO({
    title: "Marque",
    description:
      "Kit de marque Contre-épreuve : logos, symbole, favicons, avatars et guide d'utilisation à télécharger pour les partenaires et la presse.",
  });

  return (
    <div className="mx-auto max-w-6xl px-5 md:px-8 py-14 md:py-20">
      <header className="max-w-3xl">
        <p className="text-orange font-bold text-sm uppercase tracking-wide mb-4">Kit de marque</p>
        <h1 className="font-display font-black text-5xl md:text-6xl uppercase leading-[0.92] text-ink">
          La marque Contre-épreuve
        </h1>
        <div className="mt-6 prose-editorial text-lg">
          <p>
            Cette page réunit les fichiers officiels du logo Contre-épreuve à destination des
            partenaires et de la presse. Le symbole repose sur deux formes rectangulaires arrondies
            imbriquées, dont la superposition matérialise la confrontation de deux lectures d'un même
            objet.
          </p>
        </div>
        <div className="mt-6 flex flex-wrap gap-3">
          <a
            href={`${B}/README-marque.md`}
            download
            data-testid="marque-download-guide"
            className="inline-flex items-center gap-2 bg-orange text-ink font-bold uppercase tracking-wide px-6 py-3.5 rounded-[10px] hover:bg-ink hover:text-orange transition-colors duration-200"
          >
            <FileText size={17} /> Guide de marque
          </a>
        </div>
      </header>

      {/* Palette */}
      <section className="mt-14">
        <h2 className="font-display font-black text-2xl md:text-3xl uppercase text-ink leading-none border-b-2 border-ink pb-3">
          Couleurs
        </h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { name: "Encre", hex: "#111111", fg: "#F7F5F0" },
            { name: "Papier", hex: "#F7F5F0", fg: "#111111" },
            { name: "Orange", hex: "#FF4D1C", fg: "#111111" },
            { name: "Gris texte", hex: "#565656", fg: "#F7F5F0" },
          ].map((c) => (
            <div key={c.hex} className="rounded-[14px] p-6 border border-line" style={{ background: c.hex, color: c.fg }}>
              <p className="font-display font-black uppercase text-lg leading-none">{c.name}</p>
              <p className="mt-2 font-mono text-sm">{c.hex}</p>
            </div>
          ))}
        </div>
      </section>

      <Group title="Logo horizontal">
        <AssetCard
          title="Fond clair"
          note="Texte noir, symbole orange et noir. Version par défaut."
          preview={`${B}/contre-epreuve-logo-horizontal-noir.svg`}
          downloads={[
            { label: "SVG", href: `${B}/contre-epreuve-logo-horizontal-noir.svg` },
            { label: "PNG", href: `${B}/contre-epreuve-logo-horizontal.png` },
          ]}
        />
        <AssetCard
          title="Fond sombre"
          dark
          note="Texte blanc, symbole orange et blanc, baseline orange."
          preview={`${B}/contre-epreuve-logo-horizontal-blanc.svg`}
          downloads={[
            { label: "SVG", href: `${B}/contre-epreuve-logo-horizontal-blanc.svg` },
            { label: "PNG", href: `${B}/contre-epreuve-logo-horizontal-blanc.png` },
          ]}
        />
        <AssetCard
          title="Compact"
          note="Symbole et nom, sans baseline."
          preview={`${B}/contre-epreuve-logo-compact.svg`}
          downloads={[
            { label: "SVG", href: `${B}/contre-epreuve-logo-compact.svg` },
            { label: "PNG", href: `${B}/contre-epreuve-logo-compact.png` },
          ]}
        />
      </Group>

      <Group title="Symbole">
        <AssetCard
          title="Bichrome"
          note="Orange et noir. Usage principal."
          preview={`${B}/contre-epreuve-symbole.svg`}
          downloads={[
            { label: "SVG", href: `${B}/contre-epreuve-symbole.svg` },
            { label: "PNG", href: `${B}/contre-epreuve-symbole.png` },
          ]}
        />
        <AssetCard
          title="Orange"
          preview={`${B}/contre-epreuve-symbole-orange.svg`}
          downloads={[{ label: "SVG", href: `${B}/contre-epreuve-symbole-orange.svg` }]}
        />
        <AssetCard
          title="Noir"
          preview={`${B}/contre-epreuve-symbole-noir.svg`}
          downloads={[{ label: "SVG", href: `${B}/contre-epreuve-symbole-noir.svg` }]}
        />
        <AssetCard
          title="Blanc"
          dark
          preview={`${B}/contre-epreuve-symbole-blanc.svg`}
          downloads={[{ label: "SVG", href: `${B}/contre-epreuve-symbole-blanc.svg` }]}
        />
      </Group>

      <Group title="Icônes et favicons">
        <AssetCard
          title="Favicon"
          preview={`${B}/favicon.svg`}
          downloads={[
            { label: "SVG", href: `${B}/favicon.svg` },
            { label: "32px", href: `${B}/favicon-32x32.png` },
            { label: "16px", href: `${B}/favicon-16x16.png` },
          ]}
        />
        <AssetCard
          title="Icône Apple"
          dark
          note="180 x 180 px."
          preview={`${B}/apple-touch-icon.png`}
          downloads={[{ label: "PNG 180", href: `${B}/apple-touch-icon.png` }]}
        />
        <AssetCard
          title="Icônes application"
          dark
          note="Pour installation sur mobile."
          preview={`${B}/icon-192.png`}
          downloads={[
            { label: "192px", href: `${B}/icon-192.png` },
            { label: "512px", href: `${B}/icon-512.png` },
          ]}
        />
      </Group>

      <Group title="Réseaux sociaux">
        <AssetCard
          title="Avatar carré"
          dark
          note="Symbole orange et blanc sur fond noir."
          preview={`${B}/contre-epreuve-avatar-512.png`}
          downloads={[
            { label: "1024px", href: `${B}/contre-epreuve-avatar-1024.png` },
            { label: "512px", href: `${B}/contre-epreuve-avatar-512.png` },
          ]}
        />
        <AssetCard
          title="Image de partage"
          dark
          note="Open Graph, 1200 x 630 px."
          preview={`${B}/contre-epreuve-og-default-1200x630.png`}
          downloads={[{ label: "PNG", href: `${B}/contre-epreuve-og-default-1200x630.png` }]}
        />
      </Group>

      <section className="mt-16 bg-highlight rounded-[16px] p-8">
        <h2 className="font-display font-black text-2xl uppercase text-ink leading-none">Usages à éviter</h2>
        <ul className="mt-4 space-y-2 text-ink/85 leading-relaxed list-disc pl-5">
          <li>Ne pas étirer le logo ni modifier ses proportions.</li>
          <li>Ne pas modifier librement ses couleurs.</li>
          <li>Ne pas ajouter d'ombre ou d'effet 3D.</li>
          <li>Ne pas placer le logo sur un fond au contraste insuffisant.</li>
          <li>Ne pas afficher le symbole seul et le logo horizontal côte à côte inutilement.</li>
        </ul>
      </section>

      <p className="mt-12 text-sm text-muted-ink">
        Une question sur l'usage de la marque ? <Link to="/contact" className="underline underline-offset-4 hover:text-orange">Contactez Contre-épreuve</Link>.
      </p>
    </div>
  );
}
